// Cookie consent manager (shared between lvgimob.md and ipoteca.lvgimob.md).
// Cookie: lvg_cookie_consent on domain .lvgimob.md, Secure, SameSite=Lax,
// 180 days. Content: { analytics, marketing, externalMedia, date, expiry, version }.

export const CONSENT_KEY = 'lvg_cookie_consent';
export const CONSENT_VERSION = 1;
export const CONSENT_DAYS = 180;

const GA_ID = 'G-PCBBHRJRDS';

// ---- storage ----------------------------------------------------------------

function parseValue(raw) {
  try {
    return JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }
}

export function getConsent() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .map((c) => c.split('='))
    .find(([name]) => name === CONSENT_KEY);
  if (!match) return null;
  const state = parseValue(match[1]);
  if (!state || !state.expiry || Number(state.expiry) < Date.now()) return null;
  return state;
}

function writeConsent(state) {
  const payload = encodeURIComponent(JSON.stringify({
    analytics: Boolean(state.analytics),
    marketing: Boolean(state.marketing),
    externalMedia: Boolean(state.externalMedia),
    date: state.date || new Date().toISOString(),
    expiry: state.expiry || Date.now() + CONSENT_DAYS * 24 * 3600 * 1000,
    version: CONSENT_VERSION,
  }));
  const base = `${CONSENT_KEY}=${payload}; max-age=${CONSENT_DAYS * 24 * 3600}; path=/; Secure; SameSite=Lax`;
  try {
    // shared with the ipoteca subdomain
    document.cookie = `${base}; domain=.lvgimob.md`;
  } catch {
    document.cookie = base;
  }
}

function notify(state) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('lvg-consent-changed', { detail: state }));
  }
}

export function acceptAllConsent() {
  const state = { analytics: true, marketing: true, externalMedia: true, date: new Date().toISOString(), expiry: Date.now() + CONSENT_DAYS * 24 * 3600 * 1000, version: CONSENT_VERSION };
  writeConsent(state);
  notify(state);
  return state;
}

// Removes the cookies set by the non-essential services (Google Analytics, Meta).
function clearNonEssentialCookies() {
  if (typeof document === 'undefined') return;
  const names = ['_ga', '_gid', '_ga_' + GA_ID.replace('G-', ''), '_fbp', '_fbc'];
  names.forEach((n) => {
    document.cookie = `${n}=; max-age=0; path=/; domain=.lvgimob.md`;
    document.cookie = `${n}=; max-age=0; path=/`;
  });
}

export function acceptNecessaryConsent() {
  const state = { analytics: false, marketing: false, externalMedia: false, date: new Date().toISOString(), expiry: Date.now() + CONSENT_DAYS * 24 * 3600 * 1000, version: CONSENT_VERSION };
  writeConsent(state);
  clearNonEssentialCookies();
  // După retragere nu mai trimitem evenimente noi către Google Analytics.
  gaLoaded = false;
  if (typeof window !== 'undefined') window.gtag = undefined;
  notify(state);
  return state;
}

// Enables only the external services (e.g. the map button), keeps analytics as-is.
export function enableExternalMedia() {
  const current = getConsent();
  const state = {
    analytics: Boolean(current?.analytics),
    marketing: Boolean(current?.marketing),
    externalMedia: true,
    date: new Date().toISOString(),
    expiry: Date.now() + CONSENT_DAYS * 24 * 3600 * 1000,
    version: CONSENT_VERSION,
  };
  writeConsent(state);
  notify(state);
  return state;
}

// Withdrawal: remove known cookies and reset the consent.
export function revokeConsent() {
  if (typeof document === 'undefined') return;
  clearNonEssentialCookies();
  document.cookie = `${CONSENT_KEY}=; max-age=0; path=/; domain=.lvgimob.md`;
  document.cookie = `${CONSENT_KEY}=; max-age=0; path=/`;
  notify({ analytics: false, marketing: false, externalMedia: false, revoked: true });
}

// ---- Google Analytics (loaded ONLY after consent) ---------------------------

let gaLoaded = false;

export function ensureAnalytics() {
  if (typeof window === 'undefined') return;
  const state = getConsent();
  if (!state?.analytics) return;
  if (gaLoaded) return;
  gaLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: true });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
}

// Page view for SPA route changes (single init, no duplicates).
let lastTrackedPath = null;
export function trackPageView(pathname) {
  if (typeof window === 'undefined' || !window.gtag) return;
  if (pathname === lastTrackedPath) return;
  lastTrackedPath = pathname;
  window.gtag('event', 'page_view', { page_path: pathname });
}

export function onConsentChange(callback) {
  if (typeof window === 'undefined') return () => {};
  const handler = (e) => callback(e.detail);
  window.addEventListener('lvg-consent-changed', handler);
  return () => window.removeEventListener('lvg-consent-changed', handler);
}
